// Static reference data for the onboarding wizard.

export const AGE_RANGES = ["40–45", "46–50", "51–55", "56–60", "61–65", "66–70"];

export const ARCHETYPES = [
  { name: "Classic", desc: "Timeless, tailored, never trend-chasing" },
  { name: "Elegant & Refined", desc: "Understated luxury, quiet polish" },
  { name: "Modern Minimalist", desc: "Clean lines, considered simplicity" },
  { name: "Sophisticated Chic", desc: "City-smart, effortlessly put together" },
  { name: "Business Executive", desc: "Authority with softness, boardroom-ready" },
  { name: "Glamorous", desc: "Shine, drama, and a little sparkle" },
  { name: "Romantic", desc: "Soft drape, florals, gentle femininity" },
  { name: "Bohemian", desc: "Free, textured, artful layering" },
  { name: "Creative & Artsy", desc: "Unexpected shapes and color play" },
  { name: "Dramatic", desc: "Bold statements, striking silhouettes" },
  { name: "Statement Maker", desc: "One unforgettable piece per look" },
  { name: "Edgy", desc: "Structured, confident, a little rebellious" },
  { name: "Preppy", desc: "Crisp, collegiate, polished casual" },
  { name: "Relaxed Casual", desc: "Comfort-first without losing style" },
  { name: "Vintage & Retro", desc: "Old-Hollywood and decade-inspired" },
  { name: "Traditional & Cultural", desc: "Heritage prints, meaningful pieces" },
  { name: "Faith-Forward Modest", desc: "Graceful coverage, dignified elegance" },
];

export const OCCASIONS = [
  "Work & Office",
  "Church & Faith Gatherings",
  "Galas & Formal Events",
  "Networking & Business",
  "Speaking & On Stage",
  "Date Night",
  "Weddings",
  "Girls' Night Out",
  "Travel",
  "Weekend Leisure",
  "Everyday & Errands",
];

export const COMFORT = [
  "Love highlighting my waist",
  "Enjoy showing my arms",
  "Prefer graceful coverage on arms",
  "Love a bold neckline",
  "Prefer a higher neckline",
  "Love fitted, tailored styles",
  "Prefer relaxed, flowy silhouettes",
  "Enjoy shorter hemlines",
  "Prefer longer hemlines",
  "Love making a statement with color",
];

// Deep-skin-optimized swatch grid. Each maps to an engine depth bucket.
export const SKIN_SWATCHES = [
  { hex: "#C68642", depth: "light-medium", label: "Warm Honey" },
  { hex: "#A9714B", depth: "medium", label: "Golden Chestnut" },
  { hex: "#8D5524", depth: "medium", label: "Amber Bronze" },
  { hex: "#7A4B28", depth: "medium-deep", label: "Cinnamon" },
  { hex: "#6B4226", depth: "medium-deep", label: "Rich Walnut" },
  { hex: "#5A3720", depth: "deep", label: "Deep Mahogany" },
  { hex: "#48301C", depth: "deep", label: "Espresso" },
  { hex: "#3A2417", depth: "rich-deep", label: "Rich Cocoa" },
  { hex: "#2C1B12", depth: "rich-deep", label: "Deep Ebony" },
  { hex: "#1F130C", depth: "rich-deep", label: "Onyx" },
];

export const UNDERTONE_QUIZ = [
  {
    id: "veins",
    q: "In natural light, the veins on your inner wrist look…",
    options: [
      { label: "Greenish", value: "warm" },
      { label: "Blue or purple", value: "cool" },
      { label: "A mix of both / hard to tell", value: "neutral" },
    ],
  },
  {
    id: "jewelry",
    q: "Which metal makes your skin glow most?",
    options: [
      { label: "Gold", value: "warm" },
      { label: "Silver / platinum", value: "cool" },
      { label: "Both look lovely", value: "neutral" },
    ],
  },
  {
    id: "sun",
    q: "In strong sun your skin tends to…",
    options: [
      { label: "Deepen richly, rarely burns", value: "warm" },
      { label: "Can look ashy or reddened", value: "cool" },
      { label: "Deepens evenly and easily", value: "olive" },
    ],
  },
  {
    id: "whites",
    q: "Against your face, which feels more flattering?",
    options: [
      { label: "Warm cream / ivory", value: "warm" },
      { label: "Crisp pure white", value: "cool" },
      { label: "Both work", value: "neutral" },
    ],
  },
];

export function scoreUndertone(answers) {
  const tally = { warm: 0, cool: 0, neutral: 0, olive: 0 };
  Object.values(answers).forEach((v) => {
    if (tally[v] !== undefined) tally[v] += 1;
  });
  let best = "neutral";
  let max = -1;
  ["warm", "cool", "olive", "neutral"].forEach((k) => {
    if (tally[k] > max) {
      max = tally[k];
      best = k;
    }
  });
  return best;
}

export const QUICK_MEASUREMENTS = [
  { key: "height", label: "Height", guide: "Stand tall against a wall, heels down. Measure from the floor to the top of your head." },
  { key: "bust", label: "Bust", guide: "Wrap the tape around the fullest part of your bust, keeping it level across your back." },
  { key: "waist", label: "Natural waist", guide: "Find the narrowest part of your torso, usually just above the navel, and measure there." },
  { key: "hips", label: "Hips", guide: "Measure around the fullest part of your hips and seat, feet together." },
  { key: "shoulder", label: "Shoulder width", guide: "Measure across your upper back from the tip of one shoulder to the other." },
];

export const ADVANCED_MEASUREMENTS = [
  { key: "high_hip", label: "High hip", guide: "Measure around the top of your hip bones, about 3 inches below the waist." },
  { key: "torso", label: "Torso length", guide: "Measure from the top of your shoulder down to your natural waist." },
  { key: "inseam", label: "Inseam", guide: "Measure from the top of your inner thigh down to your ankle bone." },
  { key: "thigh", label: "Thigh", guide: "Measure around the fullest part of one thigh." },
  { key: "upper_arm", label: "Upper arm", guide: "Measure around the fullest part of your upper arm." },
  { key: "wrist", label: "Wrist", guide: "Measure around your wrist bone." },
  { key: "neck", label: "Neck", guide: "Measure around the base of your neck." },
  { key: "calf", label: "Calf", guide: "Measure around the fullest part of your calf." },
];
